import { useState, useEffect } from 'react';
import { financialAPI } from '../../services/api';
import toast from 'react-hot-toast';

const fmt = v => `R$ ${parseFloat(v||0).toFixed(2).replace('.',',')}`;
const inp = { width:'100%', padding:'9px 12px', border:'1.5px solid #E5E7EB', borderRadius:8, outline:'none', boxSizing:'border-box', fontSize:'.9rem' };
const lbl = { display:'block', fontWeight:600, fontSize:'.8rem', marginBottom:5, color:'#374151' };

const TYPES    = [{value:'income',label:'Receita'},{value:'expense',label:'Despesa'}];
const METHODS  = [
  {value:'cash',        label:'Dinheiro'},
  {value:'pix',         label:'PIX'},
  {value:'credit_card', label:'Cartão de Crédito'},
  {value:'debit_card',  label:'Cartão de Débito'},
  {value:'transfer',    label:'Transferência'},
  {value:'check',       label:'Cheque'},
  {value:'other',       label:'Outro'},
];
const PM_LEGACY = {dinheiro:'cash',cartao_credito:'credit_card',cartao_debito:'debit_card',transferencia:'transfer',cheque:'check',outro:'other'};
const normPM = v => PM_LEGACY[v] || v || 'pix';
const STATUS   = { pending:{ bg:'#FEF3C7',color:'#92400E',label:'Pendente' }, paid:{ bg:'#D1FAE5',color:'#065F46',label:'Pago' } };

export default function FinancialPage() {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary]           = useState({});
  const [accounts, setAccounts]         = useState([]);
  const [meta, setMeta]                 = useState({});
  const [filters, setFilters]           = useState({ type:'', status:'', account_id:'', date_from:'', date_to:'', page:1 });
  const [loading, setLoading]           = useState(false);
  const [showForm, setShowForm]         = useState(false);
  const [form, setForm]                 = useState({ description:'', type:'income', amount:'', payment_method:'pix', account_id:'', due_date:'', paid_at:'', status:'pending', category:'', notes:'' });
  const [editing, setEditing]           = useState(null);
  const [activeTab, setActiveTab]       = useState('transactions');

  const load = async () => {
    setLoading(true);
    try {
      const [tr, sm, ac] = await Promise.all([
        financialAPI.transactions({ ...filters, limit:20 }),
        financialAPI.summary(),
        financialAPI.accounts(),
      ]);
      setTransactions(tr.data.data || []);
      setMeta(tr.data.meta || {});
      setSummary(sm.data.data || {});
      setAccounts(ac.data.data || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [filters]);

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v, page: k === 'page' ? v : 1 }));
  const set  = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) await financialAPI.updateTransaction(editing.id, form);
      else         await financialAPI.createTransaction(form);
      toast.success(editing ? 'Atualizado!' : 'Lançamento criado!');
      setShowForm(false); setForm({ description:'', type:'income', amount:'', payment_method:'pix', account_id:'', due_date:'', paid_at:'', status:'pending', category:'', notes:'' }); setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.message || 'Erro.'); }
  };

  const markPaid = async (id) => {
    try { await financialAPI.markPaid(id, { paid_at: new Date().toISOString().slice(0,10) }); toast.success('Marcado como pago!'); load(); }
    catch { toast.error('Erro.'); }
  };

  const del = async (id) => {
    if (!window.confirm('Excluir lançamento?')) return;
    try { await financialAPI.deleteTransaction(id); toast.success('Excluído!'); load(); }
    catch { toast.error('Erro.'); }
  };

  const startEdit = (t) => { setEditing(t); setForm({ description:t.description, type:t.type, amount:t.amount, payment_method:normPM(t.payment_method), account_id:t.account_id||'', due_date:t.due_date||'', paid_at:t.paid_at||'', status:t.status, category:t.category||'', notes:t.notes||'' }); setShowForm(true); };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:24 }}>
        <h1 style={{ fontSize:'1.5rem', fontWeight:700, color:'#0F172A' }}>Financeiro</h1>
        <button onClick={()=>{setShowForm(true);setForm({ description:'', type:'income', amount:'', payment_method:'pix', account_id:'', due_date:'', paid_at:'', status:'pending', category:'', notes:'' });setEditing(null)}}
          style={{ background:'#2563EB', color:'#fff', border:'none', borderRadius:8, padding:'10px 20px', fontWeight:600, cursor:'pointer' }}>
          + Novo Lançamento
        </button>
      </div>

      {/* Cards resumo */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:14, marginBottom:24 }}>
        {[
          { label:'Receitas', value: summary.total_income, color:'#059669', bg:'#D1FAE5' },
          { label:'Despesas', value: summary.total_expense, color:'#DC2626', bg:'#FEE2E2' },
          { label:'Saldo', value: summary.balance, color: (summary.balance||0) >= 0 ? '#2563EB' : '#DC2626', bg:'#EFF6FF' },
          { label:'A receber', value: summary.pending_income, color:'#D97706', bg:'#FEF3C7' },
          { label:'A pagar', value: summary.pending_expense, color:'#7C3AED', bg:'#EDE9FE' },
        ].map(c => (
          <div key={c.label} style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:18 }}>
            <div style={{ fontSize:'.8rem', color:'#6B7280', marginBottom:6 }}>{c.label}</div>
            <div style={{ fontSize:'1.4rem', fontWeight:800, color: c.color }}>{fmt(c.value)}</div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display:'flex', gap:0, borderBottom:'2px solid #E5E7EB', marginBottom:16 }}>
        {[['transactions','Lançamentos'],['accounts','Contas']].map(([t,l]) => (
          <button key={t} onClick={()=>setActiveTab(t)}
            style={{ padding:'10px 20px', background:'none', border:'none', cursor:'pointer', fontWeight:700, fontSize:'.9rem',
              color: activeTab===t?'#2563EB':'#6B7280', borderBottom: activeTab===t?'2px solid #2563EB':'2px solid transparent', marginBottom:-2 }}>
            {l}
          </button>
        ))}
      </div>

      {activeTab === 'transactions' && (
        <>
          <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' }}>
            <select value={filters.type} onChange={e=>setF('type',e.target.value)} style={{ ...inp, width:'auto' }}>
              <option value="">Todos</option>
              <option value="income">Receitas</option>
              <option value="expense">Despesas</option>
            </select>
            <select value={filters.status} onChange={e=>setF('status',e.target.value)} style={{ ...inp, width:'auto' }}>
              <option value="">Todos os status</option>
              <option value="pending">Pendente</option>
              <option value="paid">Pago</option>
            </select>
            <input type="date" value={filters.date_from} onChange={e=>setF('date_from',e.target.value)} style={{ ...inp, width:'auto' }} />
            <input type="date" value={filters.date_to} onChange={e=>setF('date_to',e.target.value)} style={{ ...inp, width:'auto' }} />
            <button onClick={() => window.open(financialAPI.exportUrl({ type: filters.type, status: filters.status, date_from: filters.date_from, date_to: filters.date_to }), '_blank')}
              style={{ padding:'9px 18px', background:'#059669', color:'#fff', border:'none', borderRadius:8, fontWeight:600, cursor:'pointer', fontSize:'.9rem', whiteSpace:'nowrap' }}>
              ↓ Exportar CSV
            </button>
          </div>

          <div style={{ background:'#fff', borderRadius:12, border:'1px solid #E5E7EB', overflow:'hidden' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', minWidth:700 }}>
              <thead><tr style={{ background:'#F8FAFC', borderBottom:'1px solid #E5E7EB' }}>
                {['Descrição','Tipo','Valor','Vencimento','Status','Ações'].map(h=>(
                  <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:'.8rem', fontWeight:600, color:'#6B7280' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {loading ? <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:'#9CA3AF' }}>Carregando...</td></tr>
                : transactions.length === 0 ? <tr><td colSpan={6} style={{ padding:40, textAlign:'center', color:'#9CA3AF' }}>Nenhum lançamento.</td></tr>
                : transactions.map(t => {
                  const st = STATUS[t.status] || STATUS.pending;
                  return (
                    <tr key={t.id} style={{ borderBottom:'1px solid #F1F5F9' }}>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ fontWeight:600 }}>{t.description}</div>
                        {t.category && <div style={{ fontSize:'.75rem', color:'#9CA3AF' }}>{t.category}</div>}
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ background: t.type==='income'?'#D1FAE5':'#FEE2E2', color: t.type==='income'?'#065F46':'#991B1B', borderRadius:6, padding:'2px 8px', fontSize:'.75rem', fontWeight:600 }}>{t.type==='income'?'Receita':'Despesa'}</span>
                      </td>
                      <td style={{ padding:'12px 16px', fontWeight:700, color: t.type==='income'?'#059669':'#DC2626' }}>{fmt(t.amount)}</td>
                      <td style={{ padding:'12px 16px', fontSize:'.85rem', color:'#6B7280' }}>
                        {t.due_date ? new Date(t.due_date+'T00:00:00').toLocaleDateString('pt-BR') : '—'}
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <span style={{ background:st.bg, color:st.color, borderRadius:6, padding:'3px 10px', fontSize:'.75rem', fontWeight:600 }}>{st.label}</span>
                      </td>
                      <td style={{ padding:'12px 16px' }}>
                        <div style={{ display:'flex', gap:6 }}>
                          {t.status !== 'paid' && (
                            <button onClick={()=>markPaid(t.id)} style={{ background:'#D1FAE5', color:'#059669', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'.75rem', fontWeight:600 }}>✓ Pago</button>
                          )}
                          <button onClick={()=>startEdit(t)} style={{ background:'#EFF6FF', color:'#2563EB', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'.75rem', fontWeight:600 }}>Editar</button>
                          <button onClick={()=>del(t.id)} style={{ background:'#FEF2F2', color:'#DC2626', border:'none', borderRadius:6, padding:'4px 10px', cursor:'pointer', fontSize:'.75rem', fontWeight:600 }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {meta.total_pages > 1 && (
            <div style={{ display:'flex', justifyContent:'center', gap:8, marginTop:16 }}>
              {Array.from({ length: meta.total_pages }, (_, i) => i+1).map(p => (
                <button key={p} onClick={()=>setF('page',p)}
                  style={{ padding:'6px 14px', border:`1.5px solid ${p===filters.page?'#2563EB':'#E5E7EB'}`, borderRadius:6, background:p===filters.page?'#2563EB':'#fff', color:p===filters.page?'#fff':'#374151', cursor:'pointer', fontWeight:p===filters.page?700:400 }}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </>
      )}

      {activeTab === 'accounts' && (
        <div style={{ background:'#fff', border:'1px solid #E5E7EB', borderRadius:12, padding:24 }}>
          <h3 style={{ fontWeight:700, marginBottom:16 }}>Contas Financeiras</h3>
          {accounts.length === 0
            ? <p style={{ color:'#9CA3AF', textAlign:'center', padding:20 }}>Nenhuma conta cadastrada.</p>
            : <div style={{ display:'grid', gap:12 }}>
                {accounts.map(a => (
                  <div key={a.id} style={{ border:'1px solid #E5E7EB', borderRadius:10, padding:'14px 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                    <div>
                      <div style={{ fontWeight:700 }}>{a.name}</div>
                      <div style={{ fontSize:'.8rem', color:'#6B7280', textTransform:'capitalize' }}>{a.type}</div>
                    </div>
                    <div style={{ fontWeight:800, fontSize:'1.1rem', color:'#059669' }}>{fmt(a.balance)}</div>
                  </div>
                ))}
              </div>
          }
        </div>
      )}

      {showForm && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,.5)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:'#fff', borderRadius:16, padding:32, width:'100%', maxWidth:540, maxHeight:'90vh', overflowY:'auto' }}>
            <h3 style={{ fontWeight:700, marginBottom:20 }}>{editing?'Editar':'Novo'} Lançamento</h3>
            <form onSubmit={save}>
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Descrição *</label>
                <input value={form.description} onChange={e=>set('description',e.target.value)} required style={inp} />
              </div>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:14 }}>
                <div>
                  <label style={lbl}>Tipo *</label>
                  <select value={form.type} onChange={e=>set('type',e.target.value)} style={inp}>
                    {TYPES.map(t=><option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Valor *</label>
                  <input type="number" step="0.01" min="0" value={form.amount} onChange={e=>set('amount',e.target.value)} required style={inp} />
                </div>
                <div>
                  <label style={lbl}>Forma de pagamento</label>
                  <select value={form.payment_method} onChange={e=>set('payment_method',e.target.value)} style={inp}>
                    {METHODS.map(m=><option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
                <div>
                  <label style={lbl}>Status</label>
                  <select value={form.status} onChange={e=>set('status',e.target.value)} style={inp}>
                    <option value="pending">Pendente</option>
                    <option value="paid">Pago</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Vencimento</label>
                  <input type="date" value={form.due_date} onChange={e=>set('due_date',e.target.value)} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Data de pagamento</label>
                  <input type="date" value={form.paid_at} onChange={e=>set('paid_at',e.target.value)} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Categoria</label>
                  <input value={form.category} onChange={e=>set('category',e.target.value)} placeholder="Ex: Fornecedor" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Conta</label>
                  <select value={form.account_id} onChange={e=>set('account_id',e.target.value)} style={inp}>
                    <option value="">Sem conta</option>
                    {accounts.map(a=><option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ marginBottom:14 }}>
                <label style={lbl}>Observações</label>
                <textarea value={form.notes} onChange={e=>set('notes',e.target.value)} rows={2} style={{ ...inp, resize:'vertical' }} />
              </div>
              <div style={{ display:'flex', gap:10, justifyContent:'flex-end' }}>
                <button type="button" onClick={()=>setShowForm(false)} style={{ padding:'10px 20px', border:'1.5px solid #E5E7EB', borderRadius:8, background:'#fff', cursor:'pointer' }}>Cancelar</button>
                <button type="submit" style={{ padding:'10px 24px', background:'#2563EB', color:'#fff', border:'none', borderRadius:8, fontWeight:700, cursor:'pointer' }}>Salvar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
